import { useState, useEffect } from 'react';
import {
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
} from '@mui/material';
import ListView from '@/components/datagrid/ListView';
import useTenantQuery from '@/hooks/useTenantQuery';
import useTenantDelete from '@/hooks/useTenantDelete';
import useLiveData from '@/hooks/useLiveData';
import useTitle from '@/hooks/useTitle';
import { Delete, Add, Clear } from '@mui/icons-material';
import useAppStore from '@/store/app';
import InterfacesDetail from '@components/forms/interfaces/InterfacesDetail';

import TenantInterfaceDialog from '@/components/forms/tenants/TenantInterfaceDialog';

const INTERFACE_TYPES = [
  { value: 'nexmart', label: 'Nexmart' },
  { value: 'nexmart_media', label: 'Nexmart Media' },
  { value: 'weclapp', label: 'Weclapp' },
  { value: 'tanss', label: 'TANSS' },
  { value: 'tanss-erp', label: 'TANSS-ERP' },
  { value: 'vmware', label: 'VM-Ware' },
  { value: 'ftp', label: 'FTP' },
  { value: 'backup42', label: 'Acronis Backup42' },
  { value: 'host', label: 'Host' },
  { value: 'jira', label: 'Jira' },
  { value: 'acronis', label: 'Acronis' },
  { value: 'otto', label: 'Otto' },
  { value: 'paperless', label: 'Paperless' },
  { value: 'shipcloud', label: 'Shipcloud' },
  { value: 'riverbird', label: 'Riverbird' },
  { value: 'ups', label: 'UPS' },
  { value: 'microsoft', label: 'Microsoft' },
  { value: 'ai', label: 'AI' },
  { value: 'm365_email', label: 'M365 Email' },
];

export default function InterfaceManager() {
  useTitle('Schnittstellen');
  const [selectedRows, setSelectedRows] = useState([]);
  const [type, setType] = useState('all');
  const [status, setStatus] = useState('all');
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [sortModel] = useState([{ field: 'createdAt', sort: 'desc' }]);

  const { isLoading, data: interfaces } = useTenantQuery('interface');
  const { mutateAsync: deleteInterface } = useTenantDelete('interface');
  const { liveData } = useLiveData('interface');

  const { setDialogForm } = useAppStore();

  useEffect(() => {
    const items = [];
    if (type !== 'all') {
      items.push({
        field: 'type',
        value: type,
        operator: 'equals',
      });
    }
    if (status !== 'all') {
      items.push({
        field: 'status',
        value: status,
        operator: 'equals',
      });
    }
    setFilterModel({
      items: items.map((item, index) => ({ ...item, id: index })),
    });
  }, [type, status]);

  const handleDelete = () => {
    selectedRows.forEach((id) => {
      deleteInterface(id);
    });
    setSelectedRows([]);
  };

  const handleCloseDialog = () => {
    setDialogForm(null);
  };

  const columns = [
    {
      field: 'type',
      headerName: 'Typ',
      flex: 0.5,
      renderCell: (params) => {
        const type = INTERFACE_TYPES.find((t) => t.value === params.value);
        const expiresAt = params.row.tokenExpiresAt;
        const isExpired = expiresAt && new Date(expiresAt) < new Date();

        return (
          <Tooltip title={isExpired ? 'Token abgelaufen' : ''}>
            <Chip
              label={type?.label || params.value}
              variant="outlined"
              color={isExpired ? 'error' : 'primary'}
              sx={{ minWidth: 90, width: 90, justifyContent: 'center' }}
            />
          </Tooltip>
        );
      },
    },
    {
      field: 'description',
      headerName: 'Beschreibung',
      flex: 1,
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.3,
      renderCell: (params) => (
        <Chip
          label={params.value === 'active' ? 'Aktiv' : 'Inaktiv'}
          color={params.value === 'active' ? 'success' : 'default'}
          sx={{ minWidth: 90, width: 90, justifyContent: 'center' }}
        />
      ),
    },
  ];

  const actions = [
    <FormControl fullWidth key="type" variant="standard" sx={{ minWidth: 100 }}>
      <InputLabel id="filterSelectLabelType">Typ</InputLabel>
      <Select
        labelId="filterSelectLabelType"
        id="filterSelectType"
        value={type}
        label="Typ"
        onChange={(event) => setType(event.target.value)}
      >
        <MenuItem value="all">Alle</MenuItem>
        {INTERFACE_TYPES.map((interfaceType) => (
          <MenuItem key={interfaceType.value} value={interfaceType.value}>
            {interfaceType.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>,
    <FormControl
      fullWidth
      key="status"
      variant="standard"
      sx={{ minWidth: 100 }}
    >
      <InputLabel id="filterSelectLabelStatus">Status</InputLabel>
      <Select
        labelId="filterSelectLabelStatus"
        id="filterSelectStatus"
        value={status}
        label="Status"
        onChange={(event) => setStatus(event.target.value)}
      >
        <MenuItem value="all">Alle</MenuItem>
        <MenuItem value="active">Aktiv</MenuItem>
        <MenuItem value="inactive">Inaktiv</MenuItem>
      </Select>
    </FormControl>,
    (type !== 'all' || status !== 'all') && {
      title: 'Zurücksetzen',
      label: 'Zurücksetzen',
      icon: Clear,
      variant: 'outlined',
      onClick: () => {
        setType('all');
        setStatus('all');
      },
      group: 'primary',
    },
    {
      title: 'Hinzufügen',
      label: 'Hinzufügen',
      icon: Add,
      variant: 'contained',
      onClick: () => {
        setDialogForm(
          <TenantInterfaceDialog open={true} onClose={handleCloseDialog} />,
        );
      },
      group: 'primary',
    },
    {
      title: 'Löschen',
      label: 'Löschen',
      color: 'error',
      selection: 'multi',
      icon: Delete,
      onClick: handleDelete,
    },
  ].filter(Boolean);

  return (
    <ListView
      rows={interfaces}
      columns={columns}
      actions={actions}
      loading={isLoading}
      setSelectedRows={setSelectedRows}
      selectedRows={selectedRows}
      filterModel={filterModel}
      livedata={liveData}
      sortModel={sortModel}
      detailComponents={[
        { Component: InterfacesDetail, title: 'Interface bearbeiten' },
      ]}
    />
  );
}
